import React, { useEffect, useState } from "react";
import { api, logChuck } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, TrendingUp, Users2, Layers, Loader2 } from "lucide-react";

const DOC_TYPE_LABELS = {
  csr: "ICH E3 CSR",
  pader: "PADER",
  psur: "PSUR / PBRER",
  protocol: "Phase I/II/III Protocol",
  rems: "REMS",
  investigator_brochure: "Investigator's Brochure",
  cer: "CER (EU MDR)",
  "510k": "510(k)",
  pma: "PMA",
  de_novo: "De Novo",
  ind: "IND",
  nda: "NDA",
  anda: "ANDA",
  cmc: "CMC",
  drug_label: "Drug Label",
  eu_mdr_td: "EU MDR TD",
  ifu: "IFU",
  dhf: "DHF",
  risk_mgmt: "Risk Mgmt",
  design_controls: "Design Controls",
  cybersecurity: "Cybersecurity",
};

export default function AdminChuckLearningPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/chuck/learning", { params: { days: d } });
      setData(data);
      logChuck("admin_reviewed_learning", { payload: { window_days: d } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Chuck learning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]); // eslint-disable-line

  if (loading || !data) {
    return (
      <div data-testid="chuck-learning-loading" className="flex h-64 items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Chuck learning…
      </div>
    );
  }

  const maxDoc = Math.max(1, ...(data.top_doc_types || []).map((r) => r.n));
  const maxDay = Math.max(1, ...(data.daily_trend || []).map((r) => r.n));

  return (
    <div data-testid="chuck-learning-root" className="space-y-8">
      <div>
        <div className="overline mb-2 text-[#2563EB]">CHUCK LEARNING · LIVE</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          What Chuck is learning from.
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Every consented, non-Enterprise draft feeds Chuck. Use this to prioritize which drafters get fine-tuning next.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi testid="chuck-kpi-events-window" icon={Sparkles} label={`Events (last ${data.window_days} days)`} value={data.events_in_window} />
        <Kpi testid="chuck-kpi-events-total" icon={TrendingUp} label="Events (all time)" value={data.total_events_all_time} />
        <Kpi testid="chuck-kpi-contributors" icon={Users2} label={`Unique contributors (${data.window_days}d)`} value={data.unique_contributors_in_window} />
        <Kpi testid="chuck-kpi-consenting" icon={Layers} label="Consenting users" value={data.consenting_users} />
      </div>

      {/* Window selector */}
      <div className="flex items-center gap-2 text-xs">
        <span className="overline text-slate-500">WINDOW</span>
        {[7, 14, 30, 60, 90].map((d) => (
          <button
            key={d}
            data-testid={`chuck-window-${d}`}
            onClick={() => setDays(d)}
            className={`border px-2.5 py-1 ${days === d ? "border-[#2563EB] bg-[#2563EB]/10 text-[#1D4ED8]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}
          >
            {d}d
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Top doc types */}
        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-white p-5">
            <div className="overline mb-3 text-[#2563EB]">TOP DOCUMENT TYPES · {data.top_doc_types.length}</div>
            {data.top_doc_types.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No document-type events yet. Get a consenting user to generate a draft.
              </div>
            ) : (
              <div data-testid="chuck-top-doc-types" className="space-y-2">
                {data.top_doc_types.map((row) => (
                  <div key={row.doc_type} className="flex items-center gap-3">
                    <div className="w-40 truncate text-sm font-medium text-slate-800">
                      {DOC_TYPE_LABELS[row.doc_type] || row.doc_type}
                    </div>
                    <div className="relative h-6 flex-1 border border-slate-200 bg-slate-50">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#2563EB]/80"
                        style={{ width: `${Math.round((row.n / maxDoc) * 100)}%` }}
                      />
                      <div className="relative flex h-6 items-center justify-between px-2 text-[11px]">
                        <span className="font-mono font-semibold text-slate-900">{row.n}</span>
                        <span className="text-slate-500">{row.unique_users} user{row.unique_users === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Daily trend sparkline */}
          <div className="mt-4 border border-slate-200 bg-white p-5">
            <div className="overline mb-3 text-[#2563EB]">DAILY EVENTS · LAST {data.window_days} DAYS</div>
            <div data-testid="chuck-daily-trend" className="flex h-32 items-end gap-1">
              {data.daily_trend.map((d) => (
                <div key={d.day} className="group relative flex-1">
                  <div
                    className="w-full bg-[#2563EB]/70 transition-all hover:bg-[#1D4ED8]"
                    style={{ height: `${Math.max(2, Math.round((d.n / maxDay) * 100))}%` }}
                  />
                  <div className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap border border-slate-200 bg-white px-2 py-1 text-[10px] font-mono text-slate-700 shadow-sm group-hover:block">
                    {d.day} · {d.n}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-between text-[10px] font-mono text-slate-400">
              <span>{data.daily_trend[0]?.day}</span>
              <span>{data.daily_trend[data.daily_trend.length - 1]?.day}</span>
            </div>
          </div>
        </div>

        {/* Event types breakdown */}
        <div className="lg:col-span-5">
          <div className="border border-slate-200 bg-white p-5">
            <div className="overline mb-3 text-[#2563EB]">EVENT TYPES · ALL TIME</div>
            {data.event_types.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">No events yet.</div>
            ) : (
              <div data-testid="chuck-event-types" className="divide-y divide-slate-100">
                {data.event_types.map((r) => (
                  <div key={r.event_type} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-mono text-xs text-slate-700">{r.event_type}</span>
                    <span className="font-mono text-xs font-semibold text-slate-900">{r.n}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-600">
            <div className="mb-1 font-medium text-slate-700">How Chuck learns</div>
            Chuck ingests <b>only</b> events from signed-in users who checked "Help improve Chuck" and are <b>not</b> on Enterprise.
            All payloads are PII-scrubbed before storage. Fine-tune priority = highest doc-type + most unique contributors.
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ testid, icon: Icon, label, value }) {
  return (
    <div data-testid={testid} className="border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <span className="overline">{label}</span>
      </div>
      <div className="mt-2 font-serif text-3xl font-semibold text-slate-900">{value.toLocaleString()}</div>
    </div>
  );
}
